import { useCallback } from 'react';
import { useChatPopup } from '../../contexts/ChatPopupContext';
import { CONTEXTS } from 'figshelf-modules';

const { useMessages } = CONTEXTS;

/**
 * Helper hook to open chat popups with user information
 * Handles creating conversations if they don't exist
 */
export const useChatPopupHelper = () => {
  const { openChat } = useChatPopup();
  const { conversations, createConversation } = useMessages();

  /**
   * Open a chat popup with a specific user
   * Creates a conversation if one doesn't exist
   * @param {Object} user - User object with id, username, etc.
   */
  const openChatWithUser = useCallback(async (user) => {
    if (!user || !user.id) {
      console.error('Invalid user object provided to openChatWithUser');
      return;
    }

    try {
      // Check if conversation already exists
      const existingConversation = conversations?.find(conv => {
        if (conv.type !== 'direct') return false;
        
        return conv.participants?.some(p => p.user?.id === user.id);
      });

      if (existingConversation) {
        // Open existing conversation
        openChat(existingConversation.id);
      } else {
        // Create new conversation
        const newConversation = await createConversation([user.id], 'direct');
        if (newConversation && newConversation.id) {
          openChat(newConversation.id);
        }
      }
    } catch (error) {
      console.error('Error opening chat with user:', error);
    }
  }, [conversations, openChat, createConversation]);

  /**
   * Open a chat popup with a specific conversation ID
   * @param {string} conversationId - ID of the conversation
   */
  const openChatWithConversation = useCallback((conversationId) => {
    if (!conversationId) {
      console.error('Invalid conversation ID provided');
      return;
    }

    openChat(conversationId);
  }, [openChat]);

  return {
    openChatWithUser,
    openChatWithConversation,
  };
};

export default useChatPopupHelper;
